import Icon from '@/components/ui/AppIcon';

interface PositioningMatrix {
  category: string;
  competitors: string;
  dannyclans: string;
  differentiator: string;
}

interface CompetitivePositioningSectionProps {
  className?: string;
}

const CompetitivePositioningSection = ({ className = '' }: CompetitivePositioningSectionProps) => {
  const positioning: PositioningMatrix[] = [
    {
      category: "Service Philosophy",
      competitors: "Results-driven, performance-focused, growth-oriented",
      dannyclans: "Infrastructure-first, compliance-led, reliability-focused",
      differentiator: "We build systems, not campaigns"
    },
    {
      category: "Pricing Model",
      competitors: "Percentage of spend, performance fees, tiered packages",
      dannyclans: "Transparent prepaid infrastructure access",
      differentiator: "No hidden costs or performance cuts"
    },
    {
      category: "Client Relationship",
      competitors: "Agency manages campaigns on client's behalf",
      dannyclans: "Client manages campaigns through our infrastructure",
      differentiator: "You maintain full control"
    },
    {
      category: "Compliance Approach",
      competitors: "Reactive problem-solving when issues arise",
      dannyclans: "Proactive compliance architecture from day one",
      differentiator: "Regulatory alignment is our foundation"
    },
    {
      category: "Geographic Strategy",
      competitors: "Domestic operations with limited global reach",
      dannyclans: "Hong Kong SAR incorporation for optimal global compliance",
      differentiator: "Jurisdictional advantage by design"
    },
    {
      category: "Target Market",
      competitors: "Broad market including beginners and SMBs",
      dannyclans: "Professional advertisers and agencies only",
      differentiator: "Expert-to-expert service model"
    }
  ];

  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="max-w-3xl mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-6">
            Competitive Positioning Matrix
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Understanding where Danny Clans Media sits in the advertising ecosystem helps clarify why infrastructure-first approach creates sustainable competitive advantages.
          </p>
        </div>

        <div className="space-y-6">
          {positioning.map((item, index) => (
            <div
              key={index}
              className="bg-surface rounded-sm border border-border overflow-hidden hover:border-accent transition-colors duration-300"
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Icon
                      name="ChartBarIcon"
                      size={20}
                      variant="outline"
                      className="text-accent"
                    />
                  </div>
                  <h3 className="font-headline font-bold text-xl text-primary">
                    {item.category}
                  </h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                  <div>
                    <p className="font-accent text-xs text-text-secondary uppercase tracking-wider mb-2">
                      Traditional Competitors
                    </p>
                    <p className="font-body text-base text-text-secondary">
                      {item.competitors}
                    </p>
                  </div>
                  <div>
                    <p className="font-accent text-xs text-accent uppercase tracking-wider mb-2">
                      Danny Clans Media
                    </p>
                    <p className="font-body text-base text-primary font-medium">
                      {item.dannyclans}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="flex items-start space-x-2">
                    <Icon
                      name="LightBulbIcon"
                      size={18}
                      variant="solid"
                      className="text-success flex-shrink-0 mt-1"
                    />
                    <div>
                      <p className="font-accent text-xs text-text-secondary uppercase tracking-wider mb-1">
                        Key Differentiator
                      </p>
                      <p className="font-body text-sm text-text-primary font-medium">
                        {item.differentiator}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="p-6 bg-primary text-primary-foreground rounded-sm">
            <Icon
              name="ShieldCheckIcon"
              size={32}
              variant="solid"
              className="mb-4"
            />
            <h4 className="font-headline font-bold text-lg mb-2">
              Institutional Grade
            </h4>
            <p className="font-body text-sm opacity-90">
              We operate with the same rigor and transparency as financial services firms, not typical ad agencies.
            </p>
          </div>

          <div className="p-6 bg-accent text-accent-foreground rounded-sm">
            <Icon
              name="ServerStackIcon"
              size={32}
              variant="solid"
              className="mb-4"
            />
            <h4 className="font-headline font-bold text-lg mb-2">
              Infrastructure Focus
            </h4>
            <p className="font-body text-sm opacity-90">
              While others chase performance metrics, we build the reliable systems that make performance possible.
            </p>
          </div>

          <div className="p-6 bg-success text-success-foreground rounded-sm">
            <Icon
              name="GlobeAltIcon"
              size={32}
              variant="solid"
              className="mb-4"
            />
            <h4 className="font-headline font-bold text-lg mb-2">
              Global Compliance
            </h4>
            <p className="font-body text-sm opacity-90">
              Hong Kong SAR incorporation provides jurisdictional advantages unavailable to domestic competitors.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitivePositioningSection;